/**
 * Super-admin dispatcher. Picks the page off `location.pathname` under
 * `/admin` and renders it under a queue header inside AdminShell. `/admin`
 * itself lands on Systems; Systems and Stream are their own routes. The rest:
 *
 *   /admin/spend                                       Spend (credits, models, grants)
 *   /admin/workspaces[/:id[/:tab]]                     Workspaces (list + detail)
 *   /admin/feature-flags                               Feature flags
 *   /admin/model-routing                               Model routing
 *   /admin/platform[/runtime|readiness|releases|services]  Platform
 *   /admin/inbox[/feedback[/:id]|/deletion-requests]   Inbox
 *   /admin/prospects                                   Prospects (parked, off the nav)
 *
 * `/admin/overview` was Pulse; it folded into Systems and redirects there.
 * Every URL the old fifteen-section index used still resolves: the legacy
 * paths redirect to their new home so bookmarks and Slack links keep working.
 */

import { Navigate, useLocation, useOutletContext } from "react-router-dom";
import type { AuthSessionResponse } from "@yagni/shared";
import { Flag, Sparkles } from "lucide-react";
import { FeatureFlagsPanel } from "../components/admin/FeatureFlagsPanel.js";
import { ModelRoutingPage } from "../components/admin/modelRouting/ModelRoutingPage.js";
import { ProspectArtifactsPanel } from "../components/admin/ProspectArtifactsPanel.js";
import { SpendPage } from "./admin/OverviewPage.js";
import { WorkspacesPage, isWorkspaceTab, type WorkspaceTab } from "./admin/WorkspacesPage.js";
import { PLATFORM_TABS, PlatformPage, type PlatformTab } from "./admin/PlatformPage.js";
import { InboxPage, type InboxTab } from "./admin/InboxPage.js";
import { workspacePath } from "./admin/shared.js";

/** Old index entry → where it lives now. Matched on the first path segment. */
const LEGACY_REDIRECTS: Record<string, string> = {
  runtime: "/admin/platform/runtime",
  usage: "/admin/spend",
  credits: "/admin/workspaces",
  "model-pins": "/admin/model-routing",
  "desktop-releases": "/admin/platform/releases",
  system: "/admin/platform/services",
  builder: "/admin/platform/readiness",
  "deletion-requests": "/admin/inbox/deletion-requests",
};

type Resolved =
  | { kind: "redirect"; to: string }
  | { kind: "spend" }
  | { kind: "workspaces"; workspaceId?: string; tab: WorkspaceTab }
  | { kind: "feature-flags" }
  | { kind: "model-routing" }
  | { kind: "platform"; tab: PlatformTab }
  | { kind: "inbox"; tab: InboxTab; feedbackId?: string }
  | { kind: "prospects" }
  | { kind: "unknown" };

export function resolveAdminPath(pathname: string): Resolved {
  const segments = pathname
    .replace(/^\/admin\/?/, "")
    .split("/")
    .filter(Boolean)
    .map((s) => decodeURIComponent(s));
  const [head, second, third] = segments;

  // Systems is the admin area's front page; it is its own route (main.tsx).
  if (!head || head === "systems") return { kind: "redirect", to: "/admin/systems" };
  if (head === "overview") return { kind: "redirect", to: "/admin/systems" };
  if (head === "spend") return { kind: "spend" };
  // Enterprise billing is deleted; its addresses land on spend and credits.
  if (head === "enterprise-billing") return { kind: "redirect", to: "/admin/spend" };
  if (head === "billing") {
    return { kind: "redirect", to: second ? workspacePath(second, "credits") : "/admin/spend" };
  }
  if (head === "feedback") {
    return { kind: "redirect", to: second ? `/admin/inbox/feedback/${encodeURIComponent(second)}` : "/admin/inbox/feedback" };
  }
  if (head in LEGACY_REDIRECTS) return { kind: "redirect", to: LEGACY_REDIRECTS[head] };

  if (head === "workspaces") {
    if (!second) return { kind: "workspaces", tab: "usage" };
    if (!third) return { kind: "redirect", to: workspacePath(second, "usage") };
    if (isWorkspaceTab(third)) return { kind: "workspaces", workspaceId: second, tab: third };
    return { kind: "redirect", to: workspacePath(second, "usage") };
  }
  if (head === "feature-flags") return { kind: "feature-flags" };
  if (head === "model-routing") return { kind: "model-routing" };
  if (head === "platform") {
    const tab = PLATFORM_TABS.find((t) => t.key === second)?.key ?? "runtime";
    return { kind: "platform", tab };
  }
  if (head === "inbox") {
    if (second === "deletion-requests") return { kind: "inbox", tab: "deletion-requests" };
    return { kind: "inbox", tab: "feedback", feedbackId: third };
  }
  if (head === "prospects") return { kind: "prospects" };
  return { kind: "unknown" };
}

function pageTitle(resolved: Resolved): React.ReactNode {
  switch (resolved.kind) {
    case "spend":
      return "Spend";
    case "workspaces":
      return "Workspaces";
    case "feature-flags":
      return (
        <>
          <Flag size={18} style={{ color: "var(--accent)" }} />
          Feature Flags
        </>
      );
    case "model-routing":
      return "Model routing";
    case "platform":
      return "Platform";
    case "inbox":
      return "Inbox";
    case "prospects":
      return (
        <>
          <Sparkles size={18} style={{ color: "var(--accent)" }} />
          Prospect Artifacts
        </>
      );
    default:
      return "Admin";
  }
}

export function AdminView() {
  const location = useLocation();
  const { session } = useOutletContext<{ session: AuthSessionResponse }>();

  if (!session.user.isSuperadmin) {
    return <Navigate to="/" replace />;
  }

  const resolved = resolveAdminPath(location.pathname);
  if (resolved.kind === "redirect") {
    return <Navigate to={resolved.to} replace />;
  }
  if (resolved.kind === "unknown") {
    return <Navigate to="/admin/systems" replace />;
  }

  return (
    <div className="adminpage">
      <div className="qhd">
        <div className="top">
          <div className="title">
            <div className="label kick">Super admin</div>
            <h1 className="flex items-center gap-2">{pageTitle(resolved)}</h1>
          </div>
        </div>
      </div>
      <div>
        {resolved.kind === "spend" && <SpendPage />}
        {resolved.kind === "workspaces" && <WorkspacesPage workspaceId={resolved.workspaceId} tab={resolved.tab} />}
        {resolved.kind === "feature-flags" && <FeatureFlagsPanel />}
        {resolved.kind === "model-routing" && <ModelRoutingPage />}
        {resolved.kind === "platform" && <PlatformPage tab={resolved.tab} />}
        {resolved.kind === "inbox" && <InboxPage tab={resolved.tab} feedbackId={resolved.feedbackId} />}
        {resolved.kind === "prospects" && <ProspectArtifactsPanel />}
      </div>
    </div>
  );
}
